<template>
    <v-row>
        <v-col>
            <v-card elevation="0">
                <v-card-title>
                    {{ data.details.originTown }} <v-icon x-small class="_arrow">fas fa-arrow-right</v-icon>
                    {{ data.details.unloadingTown }}
                </v-card-title>
                <v-expansion-panels :value="display" v-model="display" multiple>
                    <v-expansion-panel v-for="category of getDocumentCategories()" :key="category" class="extension-pane">
                        <v-expansion-panel-header>
                            <v-card-subtitle class="category">
                                {{ $t(`trackandtrace.documents.document_category_code.${category}`) }}
                            </v-card-subtitle>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <v-card style="width: 100%" elevation="0">
                                <div class="content">
                                    <v-data-table
                                        :headers="headers"
                                        :items="categoryDocuments(category)"
                                        hide-default-footer
                                        class="elevation-1"
                                        multi-sort
                                    >
                                        <template v-slot:[`item.document_type`]="{ item }">
                                            <v-icon v-if="item.document_external_url">mdi-link</v-icon>
                                            <v-icon v-else :color="fileExtension(item.document_name).color">
                                                {{ fileExtension(item.document_name).icon }}
                                            </v-icon>
                                        </template>
                                        <template v-slot:[`item.document_name`]="{ item }">
                                            <v-tooltip top>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <span v-bind="attrs" v-on="on">
                                                        {{
                                                            item.document_name ||
                                                            shortenName(item.document_external_url) ||
                                                            $t('trackandtrace.documents.untitledDocument')
                                                        }}
                                                    </span>
                                                </template>
                                                <span>
                                                    {{
                                                        item.document_name ||
                                                        item.document_external_url ||
                                                        $t('trackandtrace.documents.untitledDocument')
                                                    }}
                                                </span>
                                            </v-tooltip>
                                        </template>
                                        <template v-slot:[`item.document_creation_date`]="{ item }">
                                            {{ item.document_creation_date }}
                                        </template>
                                        <template v-slot:[`item.actions`]="{ item }">
                                            <span style="align-items: left">
                                                <button v-if="isTabPreviewSupported(item)" @click="visualizeLink(item)">
                                                    <v-icon>mdi-eye</v-icon>
                                                </button>
                                                <button v-if="!item.document_external_url" @click="downloadFile(item)" class="ml-2">
                                                    <v-icon>mdi-download</v-icon>
                                                </button>
                                            </span>
                                        </template>
                                    </v-data-table>
                                </div>
                            </v-card>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-card>
        </v-col>
    </v-row>
</template>

<script>
import { mapGetters } from 'vuex';
import { getIcon, getGenericType, MAX_NAME_LENGTH, IMAGE_TYPE, PDF_TYPE, TEXT_TYPE } from '../../../utils/file-icon';
import { SHIPMENT, GET_DOCUMENTS, GET_DOCUMENT_CATEGORIES } from '../../../constants/shipment';
import { api } from '../../../utils/api';

export default {
    props: ['data'],
    data() {
        return {
            display: []
        };
    },
    computed: {
        ...mapGetters(['getClientName']),
        headers() {
            return [
                { text: '', value: 'document_type', align: 'center', width: '5%' },
                {
                    text: this.$t('trackandtrace.documents.header.document_name'),
                    value: 'document_name',
                    align: 'center'
                },
                {
                    text: this.$t('trackandtrace.documents.header.document_creation_date'),
                    value: 'document_creation_date',
                    align: 'center',
                    width: '45%'
                },
                { text: this.$t('trackandtrace.documents.header.actions'), value: 'actions', align: 'right', width: '7%' }
            ];
        }
    },
    mounted() {
        const array = Array.from(this.getDocumentCategories());
        const object = new Array(array.length);
        for (let i = 0; i < array.length; i += 1) {
            object[i] = i;
        }
        this.display = object;
    },
    methods: {
        ...mapGetters(SHIPMENT, [GET_DOCUMENTS, GET_DOCUMENT_CATEGORIES]),
        async downloadFile(item) {
            const { document_id, document_origin_code } = item;
            if (document_origin_code === 'SHA') {
                window.open(api._buildURL('document', { id: document_id, origin: document_origin_code }), '_blank');
            } else {
                const builtUrl = api._buildURL('document', { id: document_id, origin: document_origin_code });
                const URL = `${window.location.origin}${builtUrl}`;
                window.open(URL, '_blank');
            }
        },
        async visualizeLink(item) {
            const { document_id, document_origin_code, document_external_url } = item;
            if (!document_external_url) {
                window.open(api._buildURL('previewDoc', { id: document_id, origin: document_origin_code }), '_blank');
            } else {
                window.open(document_external_url);
            }
        },
        categoryDocuments(category) {
            return this.data.documents.filter(document => document.document_category_code === category);
        },
        shortenName(name) {
            return name.length < MAX_NAME_LENGTH ? name : `${name.slice(0, MAX_NAME_LENGTH)}...`;
        },
        fileExtension(name) {
            return getIcon(name?.split('.').pop());
        },
        isTabPreviewSupported(item) {
            const { document_external_url, document_name } = item;
            const type = getGenericType(document_name?.split('.').pop());
            return document_external_url || type == PDF_TYPE || type == IMAGE_TYPE || type == TEXT_TYPE;
        }
    }
};
</script>

<style lang="scss" scoped>
._arrow {
    margin: 0 10px;
}
.content {
    margin: 0 16px 15px;
}
.extension-pane {
    padding: 12px 12px 0;
}
.v-card__subtitle.category {
    color: #3b3b3b;
    font-size: 1.04em;
    font-weight: bold;
}
</style>
